/**
 * Briefing — typed endpoint helpers for the "Brief & Deep" pattern.
 *
 * BE source of truth (Track A, in-flight as of May 23, 2026):
 *   - GET ``/api/v1/briefing/inbox?window=morning|day|week``
 *     → ``DomainBrief`` (single inbox-domain brief)
 *   - GET ``/api/v1/briefing/aggregate?window=morning|day|week``
 *     → ``AggregateBriefResponse`` (master headline + per-domain briefs)
 *
 * Wire shapes mirror the Pydantic models on the BE side — snake_case for
 * field names, lower-case literal unions for enums. The TS contract is
 * intentionally tight so misalignment surfaces at the typecheck boundary.
 *
 * Defensive parsing — ``apiClient`` already throws ``ApiError`` on non-2xx,
 * which the TanStack Query hooks downstream lift to ``isError``. The hub
 * components fall back to a silent empty state on failure so a 404 (BE not
 * yet shipped) doesn't break the surface chrome.
 */

import { apiClient } from "../ApiClient.js";

// ── Domain enums (mirror BE) ─────────────────────────────────────────────

export type BriefDomain =
  | "inbox"
  | "calendar"
  | "documents"
  | "finance"
  | "tasks"
  | "crm";

export type BriefWindow = "morning" | "day" | "week";

export type BriefSeverity = "info" | "attention" | "urgent";

export type BriefActionIntent =
  | "archive_newsletters"
  | "open_triage"
  | "quick_reply_urgent"
  | "snooze_low_priority"
  | "open_deep_view";

// ── Wire shapes ───────────────────────────────────────────────────────────

export interface BriefBullet {
  /** Lucide icon name (resolved by ``resolveBriefIcon``). */
  readonly icon: string;
  readonly text: string;
  readonly severity: BriefSeverity;
  /** In-app deep link path or ``null`` when the bullet is purely informational. */
  readonly deep_link: string | null;
}

export interface BriefAction {
  readonly label: string;
  readonly intent: BriefActionIntent;
  /** Free-form payload — handler resolves intent-specific fields. */
  readonly payload: Record<string, unknown>;
  /** Optional lucide icon name. */
  readonly icon: string | null;
}

export interface DomainBrief {
  readonly domain: BriefDomain;
  readonly headline: string;
  readonly bullets: readonly BriefBullet[];
  readonly actions: readonly BriefAction[];
  /** Canonical deep link for the full domain view. */
  readonly deep_link: string;
  readonly window: BriefWindow;
  /** ISO timestamp. */
  readonly generated_at: string;
  /** 0..1 confidence score from the BE generator. */
  readonly confidence: number;
  /** ``true`` when the BE has nothing meaningful to surface. */
  readonly empty_state: boolean;
}

export interface AggregateBriefResponse {
  readonly master_headline: string;
  /**
   * Optional 3-5 line AI-composed narrative weaving every domain.
   * ``null`` when no signal or LLM failure — FE falls back to the
   * headline-only layout.
   */
  readonly overview: string | null;
  readonly briefs: readonly DomainBrief[];
  /** ISO timestamp. */
  readonly generated_at: string;
}

// ── Endpoint helpers ──────────────────────────────────────────────────────

/** GET /briefing/inbox?window=... — single inbox-domain brief. */
export async function getInboxBrief(
  window: BriefWindow = "morning",
): Promise<DomainBrief> {
  return await getDomainBrief("inbox", window);
}

/**
 * GET /briefing/<domain>?window=... — single per-domain brief.
 *
 * Generic over ``BriefDomain``: ``inbox`` is the only endpoint shipped today
 * (Track A, May 2026). Other domains 404 silently — the TanStack Query layer
 * lifts the ``ApiError`` to ``isError`` and ``BriefingSlot`` falls back to a
 * silent empty state. As each domain endpoint lands BE-side no FE change is
 * required: the same call starts returning a payload and the hub renders it.
 */
export async function getDomainBrief(
  domain: BriefDomain,
  window: BriefWindow = "morning",
): Promise<DomainBrief> {
  const params = new URLSearchParams({ window });
  return await apiClient.get<DomainBrief>(
    `/briefing/${domain}?${params.toString()}`,
  );
}

/** GET /briefing/aggregate?window=... — master headline + per-domain briefs. */
export async function getAggregateBrief(
  window: BriefWindow = "morning",
): Promise<AggregateBriefResponse> {
  const params = new URLSearchParams({ window });
  return await apiClient.get<AggregateBriefResponse>(
    `/briefing/aggregate?${params.toString()}`,
  );
}
